import { isBlockCollapsible, isCollapsed } from "@/components/tiptap/collapse/utils";
import { getLineMenu } from "@/components/tiptap/utils/LineWrapperView";

import type { Editor } from "@tiptap/core";

export const LINE_OPTION_OVERLAY = ".line-option-overlay";

/** The line of the top-level block at `pos`, which the block menu floats beside. */
export function lineWrapperAt(editor: Editor, pos: number): HTMLElement | null {
  const dom = editor.view.nodeDOM(pos);

  if (!(dom instanceof HTMLElement)) {
    return null;
  }

  return dom.matches(".line-wrapper") ? dom : dom.querySelector<HTMLElement>(".line-wrapper");
}

/**
 * The top-level block around `pos` if it has a block menu: one drawn as a line whose node doesn't
 * opt out with `lineMenu: "none"`. A collapsed bar shows its menu only `withCollapsed`.
 */
export function menuBlockAt(
  editor: Editor,
  pos: number,
  { withCollapsed = false }: { withCollapsed?: boolean } = {},
): number | null {
  const { doc } = editor.state;

  if (pos < 0 || pos > doc.content.size) {
    return null;
  }

  const index = doc.resolve(pos).index(0);

  if (index >= doc.childCount) {
    return null;
  }

  const node = doc.child(index);
  const blockPos = doc.resolve(0).posAtIndex(index, 0);
  const isCollapsedBar = isBlockCollapsible(node) && isCollapsed(node);
  const hasMenu = getLineMenu(editor, node) !== "none" && (withCollapsed || !isCollapsedBar);

  return hasMenu && lineWrapperAt(editor, blockPos) ? blockPos : null;
}

/** The block with a menu that `element`, inside the editor's content, belongs to. */
export function menuBlockFromElement(editor: Editor, element: Element): number | null {
  if (!editor.view.dom.contains(element)) {
    return null;
  }

  try {
    return menuBlockAt(editor, editor.view.posAtDOM(element, 0));
  } catch {
    // Parts of a node view that ProseMirror doesn't map to positions.
    return null;
  }
}
